import {
  DefaultNodeModel,
  DefaultPortModel,
} from "@projectstorm/react-diagrams";
import {
  BaseModelOptions,
  DeserializeEvent,
} from "@projectstorm/react-canvas-core";
import {
  Action,
  NodeDefinition,
  SettingsValues,
} from "../../editorSlice";
import { DataPortModel } from "../ports/DataPortModel";
import { DataSidePortModel } from "../ports/DataSidePortModel";
import CheckConditionValue from "../../../../utils/checkConditionValue";


export interface DataNodeModelOptions extends BaseModelOptions {
  name?: string;
  settings?: SettingsValues;
  schema?: NodeDefinition;
  runtime?: string;
  disbaled?: boolean
  isDeprecated?: boolean
}

export interface DynamicSideOut {
  id: string;
  name: string;
  schema: string | undefined;
}

export enum NodeKind {
  SOURCE = 'source',
  FUNCTION = 'function',
  SINK = 'sink',
  DELETED = 'deleted',
  NOTE = 'note'
}

export class DataNodeModel extends DefaultNodeModel {
  type!: string;
  kind!: NodeKind;
  width!: number;
  height!: number;
  code?: string;
  technicalName!: string;
  technicalType!: string;
  settings?: SettingsValues;
  schema?: NodeDefinition;
  dynamicSideOut: { [key: string]: DynamicSideOut } = {};
  description: string | undefined;
  isDisabled!: boolean;
  isDeprecated!: boolean;
  private _minWidth = 225;
  private _minHeight = 75;

  constructor(options: DataNodeModelOptions = {}) {
    super({
      name: options.name,
      color: "rgb(0,192,255)",
      type: "ts-custom-node",
      id: options.id,
    });

    if (!options.settings || !options.schema) {
      return;
    }

    this.settings = {...options.settings};
    this.schema = options.schema;
    this.kind = this.schema.kind;
    this.code = this.schema.code;
    this.technicalType = this.schema.type;
    this.technicalName = this.schema.name;
    this.isDeprecated = options.isDeprecated ?? false;
    this.isDisabled = false;
    this.width = this._minWidth;
    this.height = this._minHeight;

    // setup an in and out port
    if (this.schema.input) {
      this.schema.input.forEach((port) => {
        // @ts-ignore
        this.addPort(
              // @ts-ignore

          new DataPortModel(true, port.name, port.type, port.actions)
        );
      });
    }

    if (this.schema.output) {
      this.schema.output.forEach((port) => {
        if (port.condition) {
          if (
            this.schema?.settings[port.condition.keyName]?.default_value &&
            CheckConditionValue(
              undefined,
              port.condition.values,
              this.schema?.settings[port.condition.keyName]?.default_value
            )
          ) {
            this.addPort(
              // @ts-ignore

              new DataPortModel(false, port.name, port.type, port.actions)
            );
          }
        } else {
          this.addPort(
            // @ts-ignore

            new DataPortModel(false, port.name, port.type, port.actions)
          );
        }
      });
    }

    if (this.dynamicSideOut) {
      Object.keys(this.dynamicSideOut).forEach((port) => {
        // @ts-ignore
        this.addPort(new DataSidePortModel(false, port.name, port.id));
      });
    }

    if (this.schema && this.schema.sideout) {
      this.schema.sideout.forEach((port) => {
        this.addPort(
                  // @ts-ignore

          new DataSidePortModel(false, port.name, port.id, port.actions)
        );
        this.dynamicSideOut[port.id] = {
          id: port.id,
          name: port.name,
          schema: port.schema,
        };
      });
    }
  }

  setName(name: string) {
    this.getOptions().name = name;
    this.fireEvent({}, "NodesUpdated");
  }

  setId(name: string) {
    this.getOptions().id = name;
    this.fireEvent({}, "NodesUpdated");
  }

  setIsDisabled(isDisabled:boolean) {
    this.isDisabled = isDisabled
    this.fireEvent({ disable: isDisabled }, "NodeIsDisabled");
  }

  serialize() {
    return {
      ...super.serialize(),
      kind: this.kind,
      settings: this.settings,
      technicalName: this.technicalName,
      technicalType: this.technicalType,
      sideOut: this.dynamicSideOut,
      description: this.description,
      disabled: this.isDisabled,
      isDeprecated: this.isDeprecated,
      width: this.width,
      height: this.height
    };
  }

  deserialize(event: DeserializeEvent<this>): void {
    super.deserialize(event);
    this.settings = event.data.settings;
    this.kind = event.data.kind;
    this.technicalName = event.data.technicalName;
    this.technicalType = event.data.technicalType;
    this.dynamicSideOut = event.data.sideOut;
    this.description = event.data.description;
    this.isDisabled = event.data.disabled;
    this.isDeprecated = event.data.isDeprecated;
    this.width = event.data.width;
    this.height = event.data.height;
  }

  getName() {
    return this.getOptions().name;
  }

  getIsDisabled() {
    return this.isDisabled;
  }

  getTechnicalName() {
    return this.technicalName;
  }

  getTechnicalType() {
    return this.technicalType;
  }

  getIsDeprecated() {
    return this.isDeprecated;
  }

  getKind() {
    return this.kind;
  }

  getSettings() {
    return this.settings;
  }

  getDefinition() {
    return this.schema;
  }

  get minWidth(): number {
    return this._minWidth;
  }

  get minHeight(): number {
    return this._minHeight;
  }

  setDefinition(def: NodeDefinition) {
    this.schema = def;
    this.schema.sideout?.forEach((out) => {
      if (this.dynamicSideOut && !this.dynamicSideOut[out.id]) {
        this.dynamicSideOut[out.id] = {
          id: out.id,
          name: out.name,
          schema: out.schema,
        };
      }
    });
    this.fireEvent({ newDefinition: def }, "DefinitionsUpdated");
  }

  setSideoutOut(schema: string, name: string, id: string) {
    if (this.dynamicSideOut?.[name]) {
      this.dynamicSideOut[name] = {
        id: id,
        name: name,
        schema: schema,
      };
    }
    this.fireEvent({ newSchema: schema }, "SchemaUpdated");
  }

  addSideOut({
    id,
    name,
    actions,
    schemaAvro,
    dynamicSideOut,
  }: {
    id: string;
    name: string;
    actions?: Action[];
    schemaAvro: any;
    dynamicSideOut: boolean;
  }) {
    this.addPort(
          // @ts-ignore

      new DataSidePortModel(false, name, id, actions, dynamicSideOut)
    );
    this.dynamicSideOut[name] = { id: id, name: name, schema: schemaAvro };
    this.fireEvent(
      { newSideout: this.dynamicSideOut[name], isCreated: true },
      "SideoutsUpdated"
    );
  }

  removeSideOut(id: string, name: string) {
    // @ts-ignore
    this.getOutPorts().forEach((p) => {
      console.log(p.getOptions());
      if (p.getOptions().id === id) {
        delete this.dynamicSideOut[name];
        p.remove();
        this.removePort(p);
      }
    });
    this.fireEvent(
      { deletedSideoutId: id, isCreated: false },
      "SideoutsUpdated"
    );
  }

  removeSQLOut( name: string) {
    // @ts-ignore
    this.getOutPorts().forEach((p) => {
      if (p.getOptions().name === name) {
        p.remove();
        this.removePort(p);
      }
    });
  }

  // @ts-ignore
  getInPorts(): DataPortModel[] {
    return super.getInPorts() as DataPortModel[];
  }

  getInPort(name: string): DataPortModel | null {
    console.log("ports", super.getInPorts() as DataPortModel[]);
    for (let port of super.getInPorts() as DataPortModel[]) {
      if (port.getName() === name) return port;
    }
    return null;
  }

  getSideOutPorts(): DataSidePortModel[] {
    return (
      (this.getOutPorts().filter(
        (p) => p instanceof DataSidePortModel
      ) as DataSidePortModel[]) || []
    );
  }

  getDataOutPorts(): DataPortModel[] {
    return (
      (this.getOutPorts().filter(
        (p) => p instanceof DataPortModel
      ) as DataPortModel[]) || []
    );
  }

  getOutPort = (name: string): DefaultPortModel | undefined => {
    return this.getOutPorts().find((port) => port.getOptions().name === name);
  };

  checkIfLabelOfSideoutAlreadyExist(id: string): boolean {
    for (let node of this.getOutPorts()) {
      console.log(node.getOptions());
      if (node.getOptions()?.label === id) {
        return false;
      }
    }
    return true;
  }

  checkIfNameOfSideoutAlreadyExist(name: string): boolean {
    for (let node of this.getOutPorts()) {
      console.log(node.getOptions());
      if (node.getOptions().name === name) {
        return false;
      }
    }
    return true;
  }

  getSideOuts(): { [key: string]: DynamicSideOut | undefined } {
    return this.dynamicSideOut;
  }

  getSideOut(id: string): DynamicSideOut | undefined {
    return this.dynamicSideOut && this.dynamicSideOut[id];
  }

  updateDimensions({ width, height }: {
    width: number;
    height: number;
    }): void {
        if (this.width === undefined || this.height === undefined) {
            super.updateDimensions({ width, height });
        }
    }
}
